import { GridRowModel, ToolbarPropsOverrides } from '@mui/x-data-grid-premium';
import { get, isEqual } from 'lodash';
import React, { Dispatch, SetStateAction, useContext, useRef, useState } from 'react';
import { FormContext } from '../../common/form/FormContext';
import FormPanel from '../../common/form/FormPanel';
import { H } from '../../common/Layout';
import DataGrid from '../../common/list/DataGrid';
import { AssociatedOrganizations, toAssociatedOrgs } from '../helpers/production';

import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { failedToastMessage } from '../../common/utils/commonUtils';
import { MARKETPLACE_ID } from '../../marketplace/MarketplaceConstants';
import { useGetOrganizationDetailsLazily } from '../../organization/useOrganizationDetailsLazily';
import useAssociatedOrganizationsGridColumns, { columnVisibility } from './useAssociatedOrganizationsGridColumns';

const withFormLayout = (Component) => {
    return ({ withFormLayout, ...props }) => {
        if (withFormLayout) {
            return (
                <FormPanel>
                    <H
                        sx={{
                            height: '300px'
                        }}
                    >
                        <Component {...props} />
                    </H>
                </FormPanel>
            );
        } else {
            return <Component {...props} />;
        }
    };
};

const DataGridWithFormLayout = withFormLayout(DataGrid);

export type AssociatedOrganizationsEditorProps = {
    withFormLayout?: boolean;
    customToolbar?: ToolbarPropsOverrides[];
    onSetRows?: (rows, keyOrgs) => void;
    onDelete?: (row, keyOrgs) => void;
    setIsMarketplaceOpen?: Dispatch<SetStateAction<string | null>>;
    handleItemClick?: (item) => void;
    canAdd?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
};

export default function AssociatedOrganizationsEditor({
    withFormLayout = true,
    customToolbar = [],
    onSetRows = undefined,
    onDelete = undefined,
    setIsMarketplaceOpen,
    handleItemClick,
    canAdd = false,
    canEdit = false,
    canDelete = false
}: AssociatedOrganizationsEditorProps) {
    const { state, validation, handleChange } = useContext(FormContext);
    const [isValid, setIsValid] = useState(false);
    const defaultColumns = useAssociatedOrganizationsGridColumns(setIsValid);
    const { getOrganizationDetail } = useGetOrganizationDetailsLazily();
    const organizationsToRows = (organizations: AssociatedOrganizations[]) => {
        return organizations
            ? organizations.map((org, index) => {
                  const row = { id: org.id ?? index };
                  defaultColumns.forEach((col) => {
                      row[col.field] = get(org, col.field);
                  });
                  row['_raw'] = org;
                  return row;
              })
            : [];
    };

    const normalizeRow = (row) => ({
        id: row.id,
        organization: row.organization,
        organizationTypes: row.organizationTypes,
        organizationCategories: row.organizationCategories,
        organizationReference: row.organizationReference,
        productionRoles: row.productionRoles
    });

    const preEditRowsRef = useRef((state.associatedOrgs ?? []).map(normalizeRow));
    const handleRowEditStart = () => {
        preEditRowsRef.current = (state.associatedOrgs ?? []).map(normalizeRow);
    };

    const handleRowsChange = (newRows) => {
        if (!Array.isArray(newRows)) return;

        const mappedRows = newRows.map(normalizeRow);

        handleChange('associatedOrgs', mappedRows);
        const rowsWithOrg = mappedRows.filter((r) => r?.organizationReference?.id);
        const hasUnresolvedRows = rowsWithOrg.length < mappedRows.length;
        const hasChanged = !isEqual(mappedRows, preEditRowsRef.current);

        if (rowsWithOrg.length && !hasUnresolvedRows && hasChanged) {
            const updatedAssociatedOrgs = toAssociatedOrgs(rowsWithOrg);
            const keyOrgIds = (state?.keyOrganizations ?? []).map((ko) => ko.organizationReference.id);
            const newKeyOrgs = updatedAssociatedOrgs.filter((org) => keyOrgIds.includes(org.organizationReference.id));
            onSetRows?.(updatedAssociatedOrgs, newKeyOrgs);
            preEditRowsRef.current = mappedRows;
        }
    };

    const onDeleteRow = (deletedRow: GridRowModel) => {
        const deleteRowId = deletedRow.id;
        const newRows = state.associatedOrgs.filter((org, idx: number) => {
            const orgId = org.id ?? idx;
            return orgId !== deleteRowId;
        });
        const updatedAssociatedOrgs = toAssociatedOrgs(newRows);

        const orgCountMap = updatedAssociatedOrgs.reduce((acc, ao) => {
            const id = ao.organizationReference.id;
            acc[id] = (acc[id] || 0) + 1;
            return acc;
        }, {});

        const updatedKeyOrgs = (state.keyOrganizations ?? []).filter((ko) => {
            const id = ko.organizationReference.id;
            if (orgCountMap[id] > 0) {
                orgCountMap[id]--;
                return true;
            }
            return false;
        });

        handleChange('associatedOrgs', newRows);
        onDelete?.(updatedAssociatedOrgs, updatedKeyOrgs);
    };

    const isCellClickable = ({ row, field }) => field === ENTITY_CONSTANTS.organization.toLowerCase();

    const handleDetails = async (item: string, val: GridRowModel) => {
        if (!handleItemClick) return;
        if (typeof setIsMarketplaceOpen === 'function') {
            setIsMarketplaceOpen?.(MARKETPLACE_ID);
        }

        if (val?.organizationReference?.id) {
            handleItemClick(null);
            try {
                const organization = await getOrganizationDetail(val.organizationReference.id);
                handleItemClick(organization);
            } catch (error) {
                failedToastMessage('organization.fetch.error');
                setIsMarketplaceOpen?.(null);
            }
        }
    };

    return (
        <DataGridWithFormLayout
            id={withFormLayout ? 'associated-orgs-editor' : 'associated-orgs-tab'}
            enableInlineEditing={canEdit}
            feature={'Production'}
            entityName={ENTITY_CONSTANTS.production}
            rowCount={state.associatedOrgs ? state.associatedOrgs.length : 0}
            rows={organizationsToRows(state.associatedOrgs)}
            setRows={handleRowsChange}
            onRowEditStart={handleRowEditStart}
            columns={defaultColumns}
            defaultColumnVisibility={columnVisibility}
            forceDefaultColumnVisibility
            errors={validation?.errors?.associatedOrgs}
            serverPagination={false}
            toolbar={{ add: canAdd, custom: customToolbar }}
            withFormLayout={withFormLayout}
            onDeleteHandle={canDelete ? onDeleteRow : undefined}
            isRowValid={() => isValid}
            sortingMode="client"
            clickableColumn="name"
            isCellClickable={isCellClickable}
            onDetails={handleDetails}
        />
    );
}
